import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Paperclip, Search, CheckCircle2 } from "lucide-react";
import api, { apiError, uploadFile } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { eur } from "@/components/accounting/docUtils";
import { catsFor, catLabel, FREQUENCIES } from "./categories";

const VIOLET = "#6D5DF6";
const today = new Date().toISOString().slice(0, 10);
const emptyFor = (type) => ({ type, label: "", category: Object.keys(catsFor(type))[0], amount_ttc: "", amount_ht: "", tva: "", date: today, vendor: "", payment_method: "", receipt_url: "", note: "", frequency: "ponctuelle", next_due: "", pro_share: 100, status: "payee" });
const inp = "w-full border border-neutral-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-[#6D5DF6]";
const sel = inp;

export function ExpenseModal({ type, expense, onClose, onSaved, switchType }) {
  const [f, setF] = useState(expense ? { ...expense } : emptyFor(type));
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const cats = catsFor(f.type);
  const save = async () => {
    if (!f.label.trim() || !(+f.amount_ttc > 0)) { toast.error("Intitulé et montant TTC requis"); return; }
    setBusy(true);
    try {
      const payload = { ...f, amount_ttc: +f.amount_ttc, amount_ht: f.amount_ht === "" ? null : +f.amount_ht, tva: f.tva === "" ? null : +f.tva, pro_share: +f.pro_share || 0 };
      if (expense) await api.patch(`/pilotage/expenses/${expense.id}`, payload);
      else await api.post("/pilotage/expenses", payload);
      toast.success(expense ? "Dépense modifiée" : "Dépense ajoutée");
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const uploadReceipt = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    try { const d = await uploadFile(file); set("receipt_url", d.url); toast.success("Justificatif joint"); }
    catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="expense-modal">
        <DialogHeader><DialogTitle className="font-heading text-base">{expense ? "Modifier" : "Ajouter"} une dépense {f.type === "pro" ? "professionnelle" : "personnelle"}</DialogTitle></DialogHeader>
        {switchType && !expense && (
          <div className="flex rounded-lg border border-neutral-200 overflow-hidden text-xs mb-1">
            {[["pro", "Professionnelle"], ["perso", "Personnelle"]].map(([v, l]) => (
              <button key={v} onClick={() => { setF(emptyFor(v)); switchType(v); }} data-testid={`expense-type-${v}`}
                className={`flex-1 py-1.5 transition-colors ${f.type === v ? "text-white" : "bg-white text-neutral-500"}`} style={f.type === v ? { background: VIOLET } : {}}>{l}</button>
            ))}
          </div>
        )}
        <div className="grid grid-cols-2 gap-2.5 mt-2">
          <label className="col-span-2 text-[11px] text-neutral-500">Intitulé *
            <Input value={f.label} onChange={(e) => set("label", e.target.value)} className="h-8 text-xs mt-1" data-testid="expense-label" /></label>
          <label className="text-[11px] text-neutral-500">Catégorie
            <select value={f.category} onChange={(e) => set("category", e.target.value)} className={`${sel} mt-1`} data-testid="expense-category">
              {Object.entries(cats).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select></label>
          <label className="text-[11px] text-neutral-500">Date de paiement
            <Input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} className="h-8 text-xs mt-1" data-testid="expense-date" /></label>
          <label className="text-[11px] text-neutral-500">Montant TTC *
            <Input type="number" min="0" step="0.01" value={f.amount_ttc} onChange={(e) => set("amount_ttc", e.target.value)} className="h-8 text-xs mt-1" data-testid="expense-amount" /></label>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-[11px] text-neutral-500">dont HT
              <Input type="number" min="0" step="0.01" value={f.amount_ht ?? ""} onChange={(e) => set("amount_ht", e.target.value)} className="h-8 text-xs mt-1" data-testid="expense-ht" /></label>
            <label className="text-[11px] text-neutral-500">dont TVA
              <Input type="number" min="0" step="0.01" value={f.tva ?? ""} onChange={(e) => set("tva", e.target.value)} className="h-8 text-xs mt-1" data-testid="expense-tva" /></label>
          </div>
          <label className="text-[11px] text-neutral-500">Fournisseur
            <Input value={f.vendor} onChange={(e) => set("vendor", e.target.value)} className="h-8 text-xs mt-1" data-testid="expense-vendor" /></label>
          <label className="text-[11px] text-neutral-500">Moyen de paiement
            <Input value={f.payment_method} onChange={(e) => set("payment_method", e.target.value)} placeholder="CB, virement…" className="h-8 text-xs mt-1" data-testid="expense-payment-method" /></label>
          <label className="text-[11px] text-neutral-500">Fréquence
            <select value={f.frequency} onChange={(e) => set("frequency", e.target.value)} className={`${sel} mt-1`} data-testid="expense-frequency">
              {Object.entries(FREQUENCIES).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select></label>
          {f.frequency !== "ponctuelle" && (
            <label className="text-[11px] text-neutral-500">Prochaine échéance
              <Input type="date" value={f.next_due} onChange={(e) => set("next_due", e.target.value)} className="h-8 text-xs mt-1" data-testid="expense-next-due" /></label>
          )}
          <label className="text-[11px] text-neutral-500">Statut
            <select value={f.status} onChange={(e) => set("status", e.target.value)} className={`${sel} mt-1`} data-testid="expense-status">
              <option value="payee">Payée</option><option value="prevue">Prévue (non comptée)</option>
            </select></label>
          {f.type === "pro" && (
            <label className="text-[11px] text-neutral-500">Part professionnelle (%)
              <Input type="number" min="0" max="100" value={f.pro_share} onChange={(e) => set("pro_share", e.target.value)} className="h-8 text-xs mt-1" data-testid="expense-pro-share" /></label>
          )}
          <label className="col-span-2 text-[11px] text-neutral-500">Commentaire
            <Input value={f.note} onChange={(e) => set("note", e.target.value)} className="h-8 text-xs mt-1" data-testid="expense-note" /></label>
          <div className="col-span-2 flex items-center gap-2">
            <label className="flex items-center gap-1.5 text-[11px] border border-dashed border-neutral-300 rounded-lg px-3 py-2 cursor-pointer hover:bg-neutral-50" style={{ color: VIOLET }}>
              <input type="file" accept="image/*,.pdf" className="hidden" onChange={uploadReceipt} data-testid="expense-receipt-input" />
              <Paperclip className="w-3.5 h-3.5" /> Joindre un justificatif
            </label>
            {f.receipt_url && <a href={f.receipt_url} target="_blank" rel="noreferrer" className="text-[11px] underline" style={{ color: VIOLET }} data-testid="expense-receipt-link">Justificatif joint ✓</a>}
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose} className="h-8 text-xs" data-testid="expense-cancel">Annuler</Button>
          <Button onClick={save} disabled={busy} className="h-8 text-xs text-white" style={{ background: VIOLET }} data-testid="expense-save">{busy ? "Enregistrement…" : "Enregistrer"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ExpensesTab({ type, from, to }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [status, setStatus] = useState("");
  const [modal, setModal] = useState(null); // null | "new" | expense
  const load = () => api.get("/pilotage/expenses", { params: { type, frm: from, to } }).then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { setRows(null); load(); }, [type, from, to]); // eslint-disable-line
  const shown = (rows || []).filter((e) =>
    (!q || `${e.label} ${e.vendor}`.toLowerCase().includes(q.toLowerCase())) &&
    (!cat || e.category === cat) && (!status || e.status === status));
  const total = shown.filter((e) => e.status === "payee").reduce((s, e) => s + e.effective_amount, 0);
  const del = async (e2) => {
    const recur = (e2.frequency || "ponctuelle") !== "ponctuelle";
    if (!window.confirm(recur ? `Supprimer la charge récurrente « ${e2.label} » et toutes ses occurrences futures ?` : `Supprimer « ${e2.label} » ?`)) return;
    try { await api.delete(`/pilotage/expenses/${e2.template_id || e2.id}`); toast.success("Dépense supprimée"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const markPaid = async (e2) => {
    try { await api.patch(`/pilotage/expenses/${e2.template_id || e2.id}`, { ...e2, id: e2.template_id || e2.id, status: "payee" }); toast.success("Marquée comme payée"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const cats = catsFor(type);
  return (
    <div className="bg-white border border-neutral-100 rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid={`expenses-tab-${type}`}>
      <div className="flex flex-wrap items-center gap-2 p-4 border-b border-neutral-100">
        <p className="text-sm font-semibold text-[#0B132B]">Total du mois : <span style={{ color: VIOLET }}>{eur(total)}</span></p>
        <div className="flex-1" />
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-300" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" className="border border-neutral-200 rounded-lg pl-7 pr-2 py-1.5 text-xs w-44" data-testid="expense-search" />
        </div>
        <select value={cat} onChange={(e) => setCat(e.target.value)} className="border border-neutral-200 rounded-lg px-2 py-1.5 text-xs" data-testid="expense-filter-cat">
          <option value="">Toutes catégories</option>
          {Object.entries(cats).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-neutral-200 rounded-lg px-2 py-1.5 text-xs" data-testid="expense-filter-status">
          <option value="">Tous statuts</option><option value="payee">Payée</option><option value="prevue">Prévue</option>
        </select>
        <button onClick={() => setModal("new")} className="flex items-center gap-1.5 text-white text-xs font-medium rounded-lg px-3 py-1.5" style={{ background: VIOLET }} data-testid="expense-add">
          <Plus className="w-3.5 h-3.5" /> Ajouter une dépense
        </button>
      </div>
      {rows === null ? <p className="py-10 text-center text-xs text-neutral-400">Chargement…</p> : shown.length === 0 ? (
        <p className="py-10 text-center text-xs text-neutral-400" data-testid="expenses-empty">Aucune dépense {type === "pro" ? "professionnelle" : "personnelle"} sur la période.</p>
      ) : (
        <table className="w-full text-xs" data-testid="expenses-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wide text-neutral-400 border-b border-neutral-100">
              <th className="px-4 py-2 font-medium">Date</th><th className="px-2 py-2 font-medium">Intitulé</th>
              <th className="px-2 py-2 font-medium">Catégorie</th><th className="px-2 py-2 font-medium text-right">Montant TTC</th>
              {type === "pro" && <th className="px-2 py-2 font-medium text-right">Part pro</th>}
              <th className="px-2 py-2 font-medium">Fréquence</th><th className="px-2 py-2 font-medium">Statut</th>
              <th className="px-2 py-2 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((e) => (
              <tr key={e.id} className="border-b border-neutral-50 hover:bg-neutral-50/50" data-testid={`expense-row-${e.id}`}>
                <td className="px-4 py-2.5 text-neutral-500 whitespace-nowrap">{new Date(e.date + "T12:00:00").toLocaleDateString("fr-FR")}</td>
                <td className="px-2 py-2.5">
                  <p className="font-medium text-neutral-800">{e.label}</p>
                  {e.vendor && <p className="text-[10px] text-neutral-400">{e.vendor}</p>}
                </td>
                <td className="px-2 py-2.5"><span className="bg-neutral-100 text-neutral-500 rounded-full px-2 py-0.5 text-[10px]">{catLabel(type, e.category)}</span></td>
                <td className="px-2 py-2.5 text-right font-semibold text-neutral-800">{eur(e.amount_ttc)}</td>
                {type === "pro" && <td className="px-2 py-2.5 text-right text-neutral-500">{e.pro_share < 100 ? `${e.pro_share} % → ${eur(e.effective_amount)}` : "100 %"}</td>}
                <td className="px-2 py-2.5 text-neutral-500">{FREQUENCIES[e.frequency]}</td>
                <td className="px-2 py-2.5">
                  {e.status === "payee"
                    ? <span className="bg-emerald-50 text-emerald-600 rounded-full px-2 py-0.5 text-[10px] font-medium">Payée</span>
                    : e.virtual
                      ? <span className="bg-neutral-100 text-neutral-400 rounded-full px-2 py-0.5 text-[10px] font-medium" data-testid={`expense-scheduled-${e.id}`}>Prévue (auto)</span>
                      : <button onClick={() => markPaid(e)} className="bg-amber-50 text-amber-600 rounded-full px-2 py-0.5 text-[10px] font-medium hover:bg-amber-100" data-testid={`expense-mark-paid-${e.id}`}>Prévue → marquer payée</button>}
                </td>
                <td className="px-2 py-2.5 text-right whitespace-nowrap">
                  {e.receipt_url && <a href={e.receipt_url} target="_blank" rel="noreferrer" className="inline-block p-1 text-neutral-400 hover:text-neutral-600" title="Justificatif" data-testid={`expense-receipt-${e.id}`}><Paperclip className="w-3.5 h-3.5" /></a>}
                  <button onClick={() => setModal({ ...e, id: e.template_id || e.id })} className="p-1 text-neutral-400 hover:text-neutral-700" data-testid={`expense-edit-${e.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={() => del(e)} className="p-1 text-neutral-400 hover:text-rose-500" data-testid={`expense-delete-${e.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {modal && <ExpenseModal type={type} expense={modal === "new" ? null : modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
}
